import {
    Container,
    Image,
    MaterialContext,
    Panel,
    ProgressBar,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    Wrap,
} from '@wonderlandengine/react-ui';

import {useState, useEffect, useContext} from 'react';
import {RotBMaterialContext} from '../utils/rotb-material-context.js';
import {ScoreController} from '../../components/score-controller.js';
import {useBombTutorial} from '../hooks/usePowerup.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MalSpeech, TailDirection} from '../components/mal-speech.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {TutorialFingerTypes} from '../../enums/TutorialFingerTypes.js';
import {useAnimation} from '../hooks/useAnimation.js';

export const TutorialHUDPanel = () => {
    const rotbMats = useContext(RotBMaterialContext);
    const HUDModel = ScoreController.instance.HUDModel;
    const mat = useContext(MaterialContext);
    const [showMaxedLevelText, setShowMaxedLevelText] = useState(HUDModel.showMaxedLevel);

    const [cooldown, inventory, inventoryColor, onClick, cooldownMat] = useBombTutorial();

    const [tutorialFinger, setTutorialFinger] = useState(TutorialFingerTypes.None);

    const [rotateAnimationRef, rotateSetReference] = useAnimation({
        position: {from: [-50, 0], to: [50, 0]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    const [popAnimationRef, popSetReference] = useAnimation({
        position: {from: [0, -10], to: [0, 10]},
        scale: {from: [1.2, 1.2], to: [0.8, 0.8]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    const [swipeAnimationRef, swipeSetReference] = useAnimation({
        position: {from: [0, 400], to: [0, 0]},
        duration: 250,
        delay: 500,
        repeatDelay: 500,
        repeat: true,
    });

    const [powerupAnimationRef, powerupSetReference] = useAnimation({
        position: {from: [-20, 0], to: [0, -20]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    const [laneCount, setLaneCount] = useState(0);

    useEffect(() => {
        const onScoreModelChanged = (property: string) => {
            if (property === 'showMaxedLevel') {
                setShowMaxedLevelText(HUDModel.showMaxedLevel);
            }

            if (property === 'tutorialFinger') {
                setTutorialFinger(HUDModel.tutorialFinger);
            }
        };
        HUDModel.propertyChanged.add(onScoreModelChanged);

        return () => {
            HUDModel.propertyChanged.remove(onScoreModelChanged);
        };
    }, []);

    useEffect(() => {
        rotateSetReference(rotateAnimationRef.current);
    }, [tutorialFinger, rotateAnimationRef]);

    useEffect(() => {
        popSetReference(popAnimationRef.current);
    }, [tutorialFinger, popAnimationRef]);

    useEffect(() => {
        swipeSetReference(swipeAnimationRef.current);
    }, [tutorialFinger, swipeAnimationRef]);

    useEffect(() => {
        powerupSetReference(powerupAnimationRef.current);
    }, [tutorialFinger, powerupAnimationRef]);

    return (
        <Container
            margin={0}
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Column}
            justifyContent={Justify.FlexStart}
        >
            {tutorialFinger === TutorialFingerTypes.Powerup && (
                <Row
                    position={PositionType.Absolute}
                    alignSelf={Align.Center}
                    maxWidth={1000}
                    width="100%"
                    height="100%"
                    flexGrow={1}
                    padding={50}
                    justifyContent={Justify.FlexEnd}
                    alignItems={Align.FlexEnd}
                >
                    <Container marginRight={100} marginBottom={50} width={192} height={192}>
                        <RotatableImage
                            key="PowerupFinger"
                            ref={powerupAnimationRef}
                            width={192}
                            height={192}
                            src={UiTexturesManager.instance.finger}
                            rotation={225}
                        ></RotatableImage>
                    </Container>
                </Row>
            )}

            {tutorialFinger === TutorialFingerTypes.Swipe && (
                <Image
                    key="SwipeFinger"
                    ref={swipeAnimationRef}
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={100}
                    marginLeft={200}
                    width={160}
                    height={160}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
            {tutorialFinger === TutorialFingerTypes.PopBlob && (
                <Image
                    key="PopBlobFinger"
                    ref={popAnimationRef}
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={100}
                    width={192}
                    height={192}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
            {tutorialFinger === TutorialFingerTypes.Rotation && (
                <Image
                    ref={rotateAnimationRef}
                    key="RotationFinger"
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={150}
                    width={192}
                    height={192}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
        </Container>
    );
};
